/**
 * Display helpers for the product editor's per-size price breakdown
 * (Jake, 2026-10-06). The breakdown itself -- every cost, charge, price,
 * margin and markup at every quantity break -- is quoted by the server
 * (GET /api/campaign_product/<id>/price_breaks, built on price_for).
 *
 * The ONE ratio computed here is `fixedPriceRatios`: a FIXED price's margin
 * and markup against its server-quoted total cost, because it must follow
 * the distributor's typing before anything is saved. It is a ratio, not a
 * price, and it uses the same definitions and the same null rules as the
 * server's per-layer ratios (`price_for._layer_ratios`):
 *
 *   markup = (price - cost) / cost      margin = (price - cost) / price
 *
 * both null when the cost or the price is not above zero.
 */

const EN_DASH = "–";
const EM_DASH = "—";

/** "12-23" (en dash) for a bounded break, "500+" for the open top one. */
export function qtyRangeLabel(qtyMin, qtyMax) {
  if (qtyMax === null || qtyMax === undefined) return `${qtyMin}+`;
  if (qtyMax === qtyMin) return `${qtyMin}`;
  return `${qtyMin}${EN_DASH}${qtyMax}`;
}

/** Basis points -> "40.0%"; an em dash when the ratio does not exist. */
export function formatBasisPoints(bp) {
  if (typeof bp !== "number" || !Number.isFinite(bp)) return EM_DASH;
  return `${(bp / 100).toFixed(1)}%`;
}

function positiveInt(value) {
  return Number.isInteger(value) && value > 0;
}

function basisPoints(numerator, denominator) {
  return Math.round((numerator * 10000) / denominator);
}

/**
 * Margin and markup of a fixed price over its total cost, in basis points.
 *
 * `belowCost` is true when there is a real cost and a price under it,
 * including a deliberate $0 price.
 *
 * @param {number|null} priceCents
 * @param {number|null} costCents
 * @returns {{marginBp: number|null, markupBp: number|null, belowCost: boolean}}
 */
export function fixedPriceRatios(priceCents, costCents) {
  const hasCost = positiveInt(costCents);
  const hasPrice = Number.isInteger(priceCents) && priceCents >= 0;
  const belowCost = hasCost && hasPrice && priceCents < costCents;
  if (!hasCost || !positiveInt(priceCents)) {
    return { marginBp: null, markupBp: null, belowCost };
  }
  const gain = priceCents - costCents;
  return {
    marginBp: basisPoints(gain, priceCents),
    markupBp: basisPoints(gain, costCents),
    belowCost,
  };
}
